<template>
  <transition name="edit-step-modal" appear>
    <div
      v-if="modelValue"
      class="edit-step-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-step-modal-title"
      @mousedown.self="onCancel"
    >
      <div class="edit-step-modal__card">
        <div class="edit-step-modal__header">
          <div class="edit-step-modal__header-text">
            <span id="edit-step-modal-title" class="edit-step-modal__title">
              {{ modalTitle }}
            </span>
            <span class="edit-step-modal__subtitle">
              {{ modalSubtitle }}
            </span>
          </div>
          <button
            type="button"
            class="edit-step-modal__close-btn"
            aria-label="Close"
            :disabled="saving"
            @click="onCancel"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="edit-step-modal__section edit-step-modal__section--timestamp">
          <span class="edit-step-modal__label">{{ timestampLabel }}</span>
          <span class="edit-step-modal__timestamp" aria-readonly="true">
            {{ timestampRange }}
          </span>
        </div>

        <div class="edit-step-modal__section edit-step-modal__section--field">
          <ExTextInputWithLimit
            :value="localTitle"
            label="Title"
            :min="0"
            :max="TITLE_MAX"
            :show-char-counter="true"
            :status="saving ? 'Disabled' : 'Not focused'"
            :rules="titleRules"
            custom-width="100%"
            @input="onTitleInput"
          />
        </div>

        <div
          class="edit-step-modal__section edit-step-modal__section--field edit-step-modal__section--last"
        >
          <MemoInputWithLimit
            :value="localDescription"
            label="Description"
            :min="0"
            :max="DESCRIPTION_MAX"
            :rows="4"
            :auto-grow="false"
            :show-char-counter="true"
            :enable-base-validation="false"
            :disabled="saving"
            :status="saving ? 'Disabled' : 'Not focused'"
            custom-width="100%"
            @input="onDescriptionInput"
          />
        </div>

        <div class="edit-step-modal__footer">
          <div class="edit-step-modal__footer-btn">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              :disabled="saving"
              @click="onCancel"
            />
          </div>
          <div class="edit-step-modal__footer-btn">
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              label="Save"
              :disabled="!isSaveEnabled || saving"
              :loaderLeft="saving"
              @click="onSave"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ExTextInputWithLimit from "@components/TextInputWithLimit/ExTextInputWithLimit.vue";
import MemoInputWithLimit from "@components/MemoInputWithLimit/MemoInputWithLimit.vue";

const TITLE_MAX = 50;
const DESCRIPTION_MAX = 250;

const VARIANT_COPY = {
  step: {
    title: "Edit Step",
    subtitle: "Update the title and description for this instruction step.",
    timestampLabel: "Timestamp",
  },
  chapter: {
    title: "Edit Chapter",
    subtitle: "Update the title and description for this chapter.",
    timestampLabel: "Starts at",
  },
};

const props = defineProps({
  /** Controls modal visibility */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** Segment title — pre-filled and editable; max 50 characters */
  title: {
    type: String,
    default: "",
  },
  /** Segment description — pre-filled and editable; max 250 characters */
  description: {
    type: String,
    default: "",
  },
  /** "step" for tutorial steps (default); "chapter" for VIDEO_WITH_TOC chapters */
  variant: {
    type: String,
    default: "step",
    validator: (value) => ["step", "chapter"].includes(value),
  },
  /** Read-only timestamp from AI segmentation: a range ("0:00 - 1:00") for steps, the start label ("0:00") for chapters */
  timestampRange: {
    type: String,
    default: "",
  },
  /** Optional loading state on the Save button */
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "input", "cancel", "save"]);

const localTitle = ref(props.title || "");
const localDescription = ref(props.description || "");

const variantCopy = computed(() => VARIANT_COPY[props.variant] || VARIANT_COPY.step);
const modalTitle = computed(() => variantCopy.value.title);
const modalSubtitle = computed(() => variantCopy.value.subtitle);
const timestampLabel = computed(() => variantCopy.value.timestampLabel);

const titleRules = [
  (v) => !v || v.length <= TITLE_MAX || `Cannot exceed more than ${TITLE_MAX} characters`,
];

const isDirty = computed(
  () =>
    localTitle.value !== (props.title || "") || localDescription.value !== (props.description || "")
);

const hasRequiredContent = computed(
  () => localTitle.value.trim().length > 0 && localDescription.value.trim().length > 0
);

/** Allow typing past max — inputs show their error; Save stays off until fixed */
const isWithinLimits = computed(
  () => localTitle.value.length <= TITLE_MAX && localDescription.value.length <= DESCRIPTION_MAX
);

const isSaveEnabled = computed(
  () => isDirty.value && hasRequiredContent.value && isWithinLimits.value
);

function syncFromProps() {
  localTitle.value = props.title || "";
  localDescription.value = props.description || "";
}

function onTitleInput(value) {
  localTitle.value = value || "";
}

function onDescriptionInput(value) {
  localDescription.value = value || "";
}

function closeModal() {
  emit("update:modelValue", false);
  emit("input", false);
}

function onCancel() {
  if (props.saving) return;
  syncFromProps();
  closeModal();
  emit("cancel");
}

function onSave() {
  if (!isSaveEnabled.value || props.saving) return;
  emit("save", {
    title: localTitle.value,
    description: localDescription.value,
  });
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) syncFromProps();
  }
);
</script>

<style scoped>
.edit-step-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.edit-step-modal__card {
  width: 520px;
  max-width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #e4e4e4;
  background-color: #ffffff;
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0 0;
  background-repeat: no-repeat;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  display: flex;
  flex-direction: column;
}

.edit-step-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #e4e4e4;
}

.edit-step-modal__header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.edit-step-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.edit-step-modal__subtitle {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.edit-step-modal__close-btn {
  appearance: none;
  flex-shrink: 0;
  background: transparent;
  border: 0;
  padding: 4px;
  margin: 0;
  cursor: pointer;
  line-height: 0;
}

.edit-step-modal__close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.edit-step-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
  border-radius: 8px;
}

.edit-step-modal__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 24px;
  border-bottom: 1px solid #e4e4e4;
}

.edit-step-modal__section--timestamp {
  background-color: #f9fafb;
}

.edit-step-modal__section--field {
  background-color: #f9fafb;
}

.edit-step-modal__section--last {
  border-bottom: 0;
}

.edit-step-modal__label {
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.edit-step-modal__timestamp {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;
  user-select: text;
  cursor: default;
}

.edit-step-modal__footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 24px 24px;
  border-top: 1px solid #e4e4e4;
  background-color: #ffffff;
  box-sizing: border-box;
}

.edit-step-modal__footer-btn {
  flex: 1;
  min-width: 0;
}

.edit-step-modal-enter-active,
.edit-step-modal-leave-active {
  transition: opacity 180ms ease;
}

.edit-step-modal-enter-from,
.edit-step-modal-leave-to {
  opacity: 0;
}

.edit-step-modal-enter-active .edit-step-modal__card,
.edit-step-modal-leave-active .edit-step-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
  will-change: transform, opacity;
}

.edit-step-modal-enter-from .edit-step-modal__card,
.edit-step-modal-leave-to .edit-step-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

/* Shared inputs bring their own min-width; keep modal fields fluid */
.edit-step-modal__section ::v-deep(.ex_text_input),
.edit-step-modal__section ::v-deep(.memo_input) {
  min-width: 0;
}

/* Match design token for Title / Description labels */
.edit-step-modal__section--field ::v-deep(.main_label) {
  color: #475467;
}

/* Keep input/textarea surfaces white against the grey section fill */
.edit-step-modal__section--field ::v-deep(.v-input__slot) {
  background-color: #ffffff !important; /* override shared input slot fill on grey section */
}
</style>
